<template>
  <v-hover v-slot="{ hover }">
    <v-card
      :elevation="hover ? 8 : 2"
      class="medicine-refrigerator-card"
      rounded="lg"
    >
      <!-- Header: ชื่ออุปกรณ์ + เมนู -->
      <v-card-title class="card-header py-3 px-4">
        <div
          class="d-flex align-center flex-grow-1 min-width-0"
          :class="asset.maintenance ? 'text-h6 font-weight-bold grey--text' : 'text-h6 font-weight-bold black--text'"
        >
          <v-icon class="mr-2" size="28">mdi-fridge-industrial</v-icon>
          <span class="text-truncate">{{ asset.assetName }}</span>
          <v-icon v-if="asset.maintenance" small class="ml-1">mdi-wrench</v-icon>
          <ASSET_ALERT_ICON :asset="asset" class="ml-1" />
        </div>
        <ASSET_MENU :asset="asset" class="ml-2 flex-shrink-0" />
      </v-card-title>

      <v-divider class="my-0" />

      <v-card-text class="pa-4 card-content">
        <div class="top-meta mb-3">
          <div class="d-flex align-center flex-wrap gap-8">
            <v-chip small :color="tempStatusColor" text-color="white">{{ tempStatusText }}</v-chip>
            <div class="meta-item">
              <v-icon small>mdi-clock-outline</v-icon>
              <span>{{ lastUpdated }}</span>
            </div>
            <div class="meta-item">
              <v-icon small>mdi-map-marker</v-icon>
              <span class="text-truncate">{{ asset.location }}</span>
            </div>
          </div>
        </div>

        <div class="kpi-grid mb-3">
          <div class="kpi-card">
            <div class="kpi-label">อุณหภูมิปัจจุบัน</div>
            <div class="kpi-value">{{ currentTemperature }}<span class="kpi-unit">°C</span></div>
            <div class="kpi-sub">ช่วงปลอดภัย 2–8 °C</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">ความชื้น</div>
            <div class="kpi-value">{{ displayHumidity }}<span class="kpi-unit">%</span></div>
            <div class="kpi-sub">ค่าปัจจุบัน</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Min / Max</div>
            <div class="kpi-value small">{{ temperatureStats.minText }} / {{ temperatureStats.maxText }}<span class="kpi-unit">°C</span></div>
            <div class="kpi-sub">ในช่วงเวลาที่เลือก</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Average / Alert</div>
            <div class="kpi-value small">{{ temperatureStats.avgText }}<span class="kpi-unit">°C</span> · {{ temperatureStats.outOfRangeCount }}</div>
            <div class="kpi-sub">จุดนอกช่วงมาตรฐาน</div>
          </div>
        </div>

        <div class="chart-header mb-2">
          <div class="chart-toolbar d-flex align-center flex-wrap gap-wrap">
            <div class="toolbar-item">
              <DATE_RANGE_PICKER
                :value="dateRange"
                :time-picker="true"
                @input="onDateRangeInput"
              />
            </div>
            <div class="toolbar-item d-flex align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-switch
                    v-bind="attrs"
                    v-on="on"
                    v-model="autoRefresh"
                    hide-details
                    dense
                    class="mt-0 mr-1"
                    color="primary"
                  >
                    <template v-slot:label>
                      <span class="text-body-2">Auto Refresh</span>
                    </template>
                  </v-switch>
                </template>
                <span>อัปเดตกราฟอัตโนมัติทุก {{ refreshIntervalSeconds }} วินาที</span>
              </v-tooltip>
            </div>
            <div v-if="autoRefresh" class="toolbar-item">
              <v-select
                v-model="refreshIntervalSeconds"
                :items="refreshIntervalOptions"
                label="ความถี่"
                dense
                hide-details
                outlined
                class="interval-select"
              />
            </div>
          </div>
        </div>

        <div class="chart-panel mb-3">
          <div ref="chart" class="chart-container"></div>
        </div>

        <div class="sensor-section">
          <div class="sensor-title mb-2">Sensor ล่าสุด</div>
          <div class="sensor-grid">
            <div v-for="item in sensorEntries" :key="item.key" class="sensor-item">
              <div class="sensor-key">{{ item.key }}</div>
              <div class="sensor-value">{{ item.value }}</div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-hover>
</template>

<script>
import * as echarts from 'echarts/core'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { LineChart } from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'
import ASSET_MENU from '@/components/asset/asset_menu.vue'
import ASSET_ALERT_ICON from '@/components/asset/asset_alert_icon.vue'
import DATE_RANGE_PICKER from '@/components/common/DateRangePicker.vue'

echarts.use([GridComponent, TooltipComponent, LineChart, CanvasRenderer, UniversalTransition])

export default {
  name: 'MedicineRefrigeratorCard',
  props: {
    asset: {
      type: Object
    }
  },
  components: {
    ASSET_MENU,
    ASSET_ALERT_ICON,
    DATE_RANGE_PICKER
  },
  data() {
    const now = new Date()
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    return {
      chart: null,
      tsItems: [],
      dateRange: {
        startDate: oneDayAgo,
        endDate: now
      },
      autoRefresh: false,
      refreshIntervalSeconds: 30,
      refreshIntervalOptions: [
        { text: '15 วินาที', value: 15 },
        { text: '30 วินาที', value: 30 },
        { text: '1 นาที', value: 60 }
      ],
      refreshTimerId: null
    }
  },
  computed: {
    building() {
      return this.$store.state.User.building
    },
    parsedAssetData() {
      if (!this.asset || !this.asset.data) {
        return {}
      }
      try {
        return JSON.parse(this.asset.data)
      } catch (error) {
        return {}
      }
    },
    currentTemperature() {
      const temp = this.parsedAssetData.Temperature ?? this.parsedAssetData.temperature
      if (temp === undefined || temp === null || temp === '') return '-'
      const num = Number(temp)
      return Number.isFinite(num) ? num.toFixed(2) : temp
    },
    displayHumidity() {
      const val = this.parsedAssetData.Humidity ?? this.parsedAssetData.humidity
      if (val === undefined || val === null || val === '') return '-'
      const num = Number(val)
      return Number.isFinite(num) ? num.toFixed(2) : val
    },
    temperatureStats() {
      const series = this.chartData.filter(v => Number.isFinite(v))
      if (series.length === 0) {
        return {
          minText: '-',
          maxText: '-',
          avgText: '-',
          outOfRangeCount: 0
        }
      }
      const min = Math.min(...series)
      const max = Math.max(...series)
      const avg = series.reduce((sum, value) => sum + value, 0) / series.length
      const outOfRangeCount = series.filter(value => value < 2 || value > 8).length
      return {
        minText: min.toFixed(2),
        maxText: max.toFixed(2),
        avgText: avg.toFixed(2),
        outOfRangeCount
      }
    },
    sensorEntries() {
      const ignoredKeys = new Set(['Temperature', 'temperature', 'Humidity', 'humidity'])
      const keys = Object.keys(this.parsedAssetData || {}).filter(key => !ignoredKeys.has(key))
      const normalized = keys.map(key => {
        const raw = this.parsedAssetData[key]
        const num = Number(raw)
        const value = Number.isFinite(num) ? num.toFixed(2) : String(raw)
        return { key, value }
      })
      if (normalized.length === 0) {
        return [
          { key: 'Status', value: this.tempStatusText },
          { key: 'Samples', value: String(this.chartData.filter(v => Number.isFinite(v)).length) }
        ]
      }
      return normalized.slice(0, 8)
    },
    tempStatusColor() {
      const temp = this.parsedAssetData.Temperature ?? this.parsedAssetData.temperature
      const num = Number(temp)
      if (!Number.isFinite(num)) return 'grey'
      if (num >= 2 && num <= 8) return 'green'
      if (num > 8 && num <= 15) return 'orange'
      return 'red'
    },
    tempStatusText() {
      const temp = this.parsedAssetData.Temperature ?? this.parsedAssetData.temperature
      const num = Number(temp)
      if (!Number.isFinite(num)) return 'N/A'
      if (num >= 2 && num <= 8) return 'ปกติ'
      if (num > 8 && num <= 15) return 'อุ่นเกิน'
      return 'อันตราย'
    },
    lastUpdated() {
      if (!this.asset || !this.asset.heartbeat) return '-'
      return this.$dayjs(this.asset.heartbeat).format('LLL')
    },
    chartCategory() {
      return this.tsItems.map(x => {
        const label = x.date && x.H !== undefined
          ? this.$dayjs(x.date + ' ' + x.H + ':00', 'YY-MM-DD H:mm').format('HH:mm')
          : this.$dayjs(x.timestamp || x.date).format('HH:mm')
        return label
      })
    },
    chartData() {
      return this.tsItems.map(x => {
        const val = x.Temperature ?? x.temperature ?? x.Unit ?? x.value
        const num = Number(val)
        return Number.isFinite(num) ? num : null
      })
    }
  },
  mounted() {
    this.getTSData()
    this.syncAutoRefresh()
  },
  beforeDestroy() {
    this.clearRefreshTimer()
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    onDateRangeInput(range) {
      if (range && range.startDate && range.endDate) {
        this.dateRange = { startDate: range.startDate, endDate: range.endDate }
        this.getTSData()
      }
    },
    clearRefreshTimer() {
      if (this.refreshTimerId) {
        clearInterval(this.refreshTimerId)
        this.refreshTimerId = null
      }
    },
    syncAutoRefresh() {
      this.clearRefreshTimer()
      if (this.autoRefresh && this.asset && this.asset.assetId) {
        const ms = Math.max(5000, this.refreshIntervalSeconds * 1000)
        this.refreshTimerId = setInterval(() => {
          this.updateDateRangeToNow()
          this.getTSData()
        }, ms)
      }
    },
    /** อัปเดต dateRange ให้ end = เวลาปัจจุบัน และคงระยะช่วงเวลาเดิม (กราฟล่วงหน้าเวลาปัจจุบัน) */
    updateDateRangeToNow() {
      const start = this.dateRange.startDate && this.dateRange.startDate.getTime ? this.dateRange.startDate.getTime() : 0
      const end = this.dateRange.endDate && this.dateRange.endDate.getTime ? this.dateRange.endDate.getTime() : Date.now()
      const durationMs = Math.max(0, end - start)
      const now = new Date()
      this.dateRange = {
        startDate: new Date(now.getTime() - durationMs),
        endDate: now
      }
    },
    getTSData() {
      const assetId = this.asset.assetId
      if (!assetId) return
      const start = Math.floor((this.dateRange.startDate && this.dateRange.startDate.getTime) ? this.dateRange.startDate.getTime() : this.$dayjs().subtract(1, 'day').valueOf()) / 1000
      const end = Math.floor((this.dateRange.endDate && this.dateRange.endDate.getTime) ? this.dateRange.endDate.getTime() : this.$dayjs().valueOf()) / 1000

      this.$store.dispatch('MedicineRefrigerator/GET_TELEMETRY', { assetId, start, end })
        .then((data) => {
          const raw = Array.isArray(data) ? data : (data && (data.data || data.items || [])) || []
          const items = Array.isArray(raw) ? raw : []
          this.tsItems = [...items].sort((a, b) => {
            const tsA = a.timestamp || (a.date && a.H !== undefined ? this.$dayjs(a.date + ' ' + a.H + ':00').valueOf() : 0)
            const tsB = b.timestamp || (b.date && b.H !== undefined ? this.$dayjs(b.date + ' ' + b.H + ':00').valueOf() : 0)
            return tsA - tsB
          })
          this.$nextTick(() => this.setChart())
        })
        .catch(() => {
          this.tsItems = []
        })
    },
    setChart() {
      if (!this.$refs.chart || this.chartData.length === 0) return

      if (this.chart) this.chart.dispose()
      this.chart = echarts.init(this.$refs.chart)
      this.chart.setOption({
        xAxis: { type: 'category', data: this.chartCategory },
        yAxis: {
          type: 'value',
          min: (v) => Math.min(v.min, 0),
          max: (v) => Math.max(v.max, 15),
          axisLabel: { show: false },
          axisTick: { show: false },
          axisLine: { show: false },
          splitLine: {
            lineStyle: { type: 'dashed', opacity: 0.3 }
          }
        },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => (params[0] && params[0].value != null) ? params[0].value + ' °C' : '-'
        },
        series: [{
          data: this.chartData,
          type: 'line',
          smooth: true,
          areaStyle: { opacity: 0.2 }
        }],
        grid: { top: 16, right: 8, bottom: 22, left: 6, containLabel: false }
      })
    }
  },
  watch: {
    chartData: {
      handler() {
        this.$nextTick(() => this.setChart())
      }
    },
    autoRefresh() {
      this.syncAutoRefresh()
    },
    refreshIntervalSeconds() {
      this.syncAutoRefresh()
    }
  }
}
</script>

<style scoped>
.medicine-refrigerator-card {
  height: 100%;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
}

.medicine-refrigerator-card:hover {
  border-color: rgba(33, 150, 243, 0.2);
}

.medicine-refrigerator-card .card-header {
  min-height: 52px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%);
}

.card-content {
  background: linear-gradient(180deg, #ffffff 0%, #f9fbff 100%);
}

.gap-8 {
  gap: 8px;
}

.top-meta {
  border: 1px solid rgba(33, 150, 243, 0.15);
  background: rgba(33, 150, 243, 0.05);
  border-radius: 10px;
  padding: 8px 10px;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(0, 0, 0, 0.75);
  font-size: 0.76rem;
  min-width: 0;
}

.meta-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.kpi-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  background: #fff;
  padding: 10px;
}

.kpi-label {
  font-size: 0.68rem;
  color: rgba(0, 0, 0, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2933;
  line-height: 1.2;
}

.kpi-value.small {
  font-size: 1rem;
}

.kpi-unit {
  font-size: 0.8rem;
  margin-left: 2px;
  color: rgba(0, 0, 0, 0.58);
}

.kpi-sub {
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.46);
  margin-top: 3px;
}

.chart-toolbar {
  gap: 10px;
}

.toolbar-item {
  flex: 0 0 auto;
}

.interval-select {
  max-width: 130px;
}

.chart-panel {
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  border-radius: 12px;
  padding: 8px 8px 2px;
}

.chart-container {
  width: 100%;
  height: 180px;
  min-height: 170px;
}

.sensor-title {
  font-size: 0.78rem;
  font-weight: 600;
  color: #334155;
}

.sensor-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.sensor-item {
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.92);
}

.sensor-key {
  font-size: 0.68rem;
  color: rgba(0, 0, 0, 0.52);
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sensor-value {
  font-size: 0.86rem;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .sensor-grid {
    grid-template-columns: 1fr;
  }
}
</style>
